import { ReactElement, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { ChevronLeftIcon, PencilIcon } from '@navikt/aksel-icons';
import classes from './PartnerDetailPage.module.css';
import { Routes } from '../../../enums/Routes';
import { useGetPartnerDetailQuery } from '../../../hooks/queries/useGetPartnerDetailQuery';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { Button } from '../../../components/Button/Button';
import { formatNorwegianDate } from '../../../utils/dateFormatters';

const ERROR_MESSAGE = 'Kunne ikke laste partnerdata. Prøv igjen senere.';
const NOT_FOUND_MESSAGE = 'Partner ikke funnet.';

const formatCashbackPercentage = (basisPoints: number | null): string => {
  if (basisPoints === null) return 'Ikke satt';
  return `${(basisPoints / 100).toFixed(0)}%`;
};

type TabType = 'overview' | 'users' | 'customers' | 'finances' | 'contact';

export const PartnerDetailPage = (): ReactElement => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const { data: partnerDetail, isLoading, error } = useGetPartnerDetailQuery(Number(partnerId));
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  if (isLoading) {
    return <GhostLoader />;
  }

  if (error) {
    return (
      <div className={classes.container}>
        <Alert variant="danger" size="md">
          <Paragraph>{ERROR_MESSAGE}</Paragraph>
        </Alert>
      </div>
    );
  }

  if (!partnerDetail) {
    return (
      <div className={classes.container}>
        <Alert variant="warning" size="md">
          <Paragraph>{NOT_FOUND_MESSAGE}</Paragraph>
        </Alert>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as const, label: 'Oversikt' },
    { id: 'users' as const, label: 'Brukere' },
    { id: 'customers' as const, label: 'Kunder' },
    { id: 'finances' as const, label: 'Økonomi' },
    { id: 'contact' as const, label: 'Kontaktinfo' },
  ];

  return (
    <div className={classes.container}>
      <Link to={Routes.SystemAdminPartners} className={classes.backLink}>
        <ChevronLeftIcon aria-hidden="true" fontSize="20px" />
        <span>Tilbake til partnere</span>
      </Link>

      <div className={classes.header}>
        <div>
          <Heading as="h1">{partnerDetail.name}</Heading>
          <Paragraph size="lg">
            Partner siden {formatNorwegianDate(partnerDetail.createdAt)} · <strong>{partnerDetail.stats.totalCustomers}</strong> kunder
          </Paragraph>
        </div>
        <Button
          variant="secondary"
          size="small"
          onClick={() => navigate(`/system-admin/partners/${partnerId}/edit`)}
        >
          <PencilIcon aria-hidden="true" fontSize="18px" />
          Rediger partner
        </Button>
      </div>

      <div className={classes.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${classes.tab} ${activeTab === tab.id ? classes.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={classes.tabContent}>
        {activeTab === 'overview' && (
          <div className={classes.statsGrid}>
            <ContainerCard>
              <ContainerCard.Title>Totalt antall kunder</ContainerCard.Title>
              <ContainerCard.Body className={classes.statBody}>
                <div className={classes.statValue}>{partnerDetail.stats.totalCustomers}</div>
                <div className={classes.statMeta}>{partnerDetail.stats.activeCustomers} aktive</div>
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>Partner-brukere</ContainerCard.Title>
              <ContainerCard.Body className={classes.statBody}>
                <div className={classes.statValue}>{partnerDetail.users.length}</div>
                <div className={classes.statMeta}>
                  {partnerDetail.users.filter(u => !u.isPending).length} aktive
                </div>
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>Totalt antall sluttbrukere</ContainerCard.Title>
              <ContainerCard.Body className={classes.statBody}>
                <div className={classes.statValue}>{partnerDetail.stats.totalEndUsers}</div>
                <div className={classes.statMeta}>på tvers av kunder</div>
              </ContainerCard.Body>
            </ContainerCard>

            <ContainerCard>
              <ContainerCard.Title>Totalt antall team</ContainerCard.Title>
              <ContainerCard.Body className={classes.statBody}>
                <div className={classes.statValue}>{partnerDetail.stats.totalTeams}</div>
                <div className={classes.statMeta}>på tvers av kunder</div>
              </ContainerCard.Body>
            </ContainerCard>
          </div>
        )}

        {activeTab === 'users' && (
          <ContainerCard>
            <ContainerCard.Title>Partner-brukere</ContainerCard.Title>
            <ContainerCard.Body>
              <Paragraph>
                Her vises alle brukere som er ansatt hos partneren og har tilgang til partner-portalen.
              </Paragraph>
              <div className={classes.tableWrapper}>
                <table className={classes.dataTable}>
                  <thead>
                    <tr>
                      <th>E-post</th>
                      <th>Rolle</th>
                      <th>Status</th>
                      <th>Opprettet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerDetail.users.map((user) => (
                      <tr key={user.id}>
                        <td><strong>{user.email}</strong></td>
                        <td>{user.role}</td>
                        <td>{user.isPending ? 'Venter' : 'Aktiv'}</td>
                        <td>{formatNorwegianDate(user.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ContainerCard.Body>
          </ContainerCard>
        )}

        {activeTab === 'customers' && (
          <ContainerCard>
            <ContainerCard.Title>Kunder tilknyttet partner</ContainerCard.Title>
            <ContainerCard.Body>
              <Paragraph>
                Oversikt over alle kunder som er knyttet til {partnerDetail.name}.
              </Paragraph>
              <div className={classes.tableWrapper}>
                <table className={classes.dataTable}>
                  <thead>
                    <tr>
                      <th>Kundenavn</th>
                      <th>Antall brukere</th>
                      <th>Antall team</th>
                      <th>Opprettet</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerDetail.customers.map((customer) => (
                      <tr key={customer.id}>
                        <td><strong>{customer.name}</strong></td>
                        <td>{customer.userCount}</td>
                        <td>{customer.teamCount}</td>
                        <td>{formatNorwegianDate(customer.createdAt)}</td>
                        <td>
                          <span className={classes.statusBadge}>{customer.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ContainerCard.Body>
          </ContainerCard>
        )}

        {activeTab === 'finances' && (
          <ContainerCard>
            <ContainerCard.Title>Økonomi og oppgjør</ContainerCard.Title>
            <ContainerCard.Body>
              <div className={classes.infoGrid}>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Cashback-prosent:</span>
                  <span className={classes.infoValue}>
                    <strong>{formatCashbackPercentage(partnerDetail.cashbackPercentage)}</strong>
                  </span>
                </div>
              </div>
              <Paragraph size="sm" style={{ marginTop: '1rem' }}>
                Oppgjør og fakturering vil vises her når funksjonaliteten er implementert.
              </Paragraph>
            </ContainerCard.Body>
          </ContainerCard>
        )}

        {activeTab === 'contact' && (
          <ContainerCard>
            <ContainerCard.Title>Kontaktinformasjon</ContainerCard.Title>
            <ContainerCard.Body>
              <div className={classes.infoGrid}>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Organisasjonsnavn:</span>
                  <span className={classes.infoValue}>{partnerDetail.name}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Organisasjonsnummer:</span>
                  <span className={classes.infoValue}>{partnerDetail.orgNumber || 'Ikke oppgitt'}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Partner siden:</span>
                  <span className={classes.infoValue}>{formatNorwegianDate(partnerDetail.createdAt)}</span>
                </div>
              </div>
            </ContainerCard.Body>
          </ContainerCard>
        )}
      </div>
    </div>
  );
};
